import { useEffect, useMemo, useRef, useState } from 'react';
import { api, type DuplicateCandidate, type DuplicateLink } from '../api';
import { formatEventRange } from '../format';

function isoDay(date: Date) {
  return date.toISOString().slice(0, 10);
}

function CandidateDetails({ candidate }: { candidate: DuplicateCandidate }) {
  return (
    <article className="duplicate-candidate">
      <h3>{candidate.title}</h3>
      <dl>
        <div>
          <dt>Time</dt>
          <dd>{formatEventRange(candidate)}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>
            {candidate.physicalLocation
              ? `${candidate.physicalLocation.name}, ${candidate.physicalLocation.address}`
              : 'No physical location'}
          </dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>
            {candidate.calendarName} ({candidate.sourceId})
          </dd>
        </div>
      </dl>
    </article>
  );
}

export function DuplicateEventReview({
  csrfToken,
  onNotice,
  onError,
}: {
  csrfToken?: string | null;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}) {
  type RelatedChoice = {
    link: DuplicateLink;
    selected: DuplicateCandidate;
    counterpart: DuplicateCandidate;
  };
  const now = new Date();
  const [from, setFrom] = useState(isoDay(new Date(now.getTime() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(new Date(now.getTime() + 60 * 86400000)));
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [firstId, setFirstId] = useState('');
  const [secondId, setSecondId] = useState('');
  const [retainedId, setRetainedId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [link, setLink] = useState<DuplicateLink>();
  const [relatedChoices, setRelatedChoices] = useState<RelatedChoice[]>([]);
  const [inspecting, setInspecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const inspection = useRef(0);
  const first = candidates.find((candidate) => candidate.id === firstId);
  const second = candidates.find((candidate) => candidate.id === secondId);
  const pair = useMemo(
    () => (first && second && first.id !== second.id ? [first, second] : []),
    [first, second],
  );
  useEffect(
    () => () => {
      inspection.current++;
    },
    [],
  );

  const inspectLinks = async (selected: DuplicateCandidate[]) => {
    const token = ++inspection.current;
    setLink(undefined);
    setRelatedChoices([]);
    const recordsToInspect = [...new Map(selected.map((record) => [record.id, record])).values()];
    if (!recordsToInspect.length) {
      setInspecting(false);
      return;
    }
    setInspecting(true);
    try {
      const responses = await Promise.all(
        recordsToInspect.map((record) => api.eventLinks(record.id)),
      );
      if (token !== inspection.current) return;
      const returnedRecords = responses.flatMap((response) => response.records);
      setCandidates((current) => [
        ...current.map(
          (record) => returnedRecords.find((returned) => returned.id === record.id) ?? record,
        ),
        ...returnedRecords.filter((record) => !current.some((item) => item.id === record.id)),
      ]);
      const relationships = [
        ...new Map(
          responses
            .flatMap((response) => response.links)
            .map((relationship) => [relationship.duplicateId, relationship]),
        ).values(),
      ];
      const selectedIds = new Set(recordsToInspect.map((record) => record.id));
      const relationship = relationships.find(
        (item) => selectedIds.has(item.canonicalId) && selectedIds.has(item.duplicateId),
      );
      setLink(relationship);
      if (!relationship) {
        setRelatedChoices(
          relationships.flatMap((item) => {
            const selectedRecord = recordsToInspect.find((record) =>
              [item.canonicalId, item.duplicateId].includes(record.id),
            );
            const counterpartId =
              selectedRecord?.id === item.canonicalId ? item.duplicateId : item.canonicalId;
            const counterpart = returnedRecords.find((record) => record.id === counterpartId);
            return selectedRecord && counterpart
              ? [{ link: item, selected: selectedRecord, counterpart }]
              : [];
          }),
        );
      }
    } catch (cause) {
      if (token !== inspection.current) return;
      onError(cause instanceof Error ? cause.message : 'Duplicate relationships could not load');
    } finally {
      if (token === inspection.current) setInspecting(false);
    }
  };

  const search = async () => {
    setBusy(true);
    onError('');
    try {
      const result = await api.duplicateCandidates(from, to, query);
      setCandidates(result.candidates);
      setFirstId('');
      setSecondId('');
      setRetainedId('');
      setConfirmed(false);
      setLink(undefined);
      setRelatedChoices([]);
      inspection.current++;
      setInspecting(false);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Source records could not be searched');
    } finally {
      setBusy(false);
    }
  };

  const createLink = async () => {
    if (!first || !second || !retainedId || !confirmed) return;
    setBusy(true);
    onError('');
    try {
      const canonical = retainedId === first.id ? first : second;
      const duplicate = retainedId === first.id ? second : first;
      const result = await api.linkEvents(canonical, duplicate, csrfToken);
      setCandidates((current) =>
        current.map((candidate) => {
          if (candidate.id === result.canonicalEvent.id)
            return { ...candidate, version: result.canonicalEvent.version };
          if (candidate.id === result.duplicateEvent.id)
            return { ...candidate, version: result.duplicateEvent.version };
          return candidate;
        }),
      );
      setLink(result.link);
      onNotice('Duplicate records linked. Both source records remain available.');
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Duplicate records could not be linked');
    } finally {
      setBusy(false);
    }
  };

  const removeLink = async () => {
    if (!link || !first || !second) return;
    const canonical = [first, second].find((candidate) => candidate.id === link.canonicalId);
    const duplicate = [first, second].find((candidate) => candidate.id === link.duplicateId);
    if (!canonical || !duplicate) return;
    setBusy(true);
    onError('');
    try {
      const result = await api.unlinkEvents(link, canonical.version, duplicate.version, csrfToken);
      setCandidates((current) =>
        current.map((candidate) => {
          if (candidate.id === result.canonicalEvent.id)
            return { ...candidate, version: result.canonicalEvent.version };
          if (candidate.id === result.duplicateEvent.id)
            return { ...candidate, version: result.duplicateEvent.version };
          return candidate;
        }),
      );
      setLink(undefined);
      setConfirmed(false);
      onNotice('Duplicate link removed. Both source records remain available.');
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Duplicate link could not be removed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-card duplicate-review">
      <h2>Duplicate event review</h2>
      <p>
        Search a bounded date range, compare two imported records, then choose which record appears
        in combined calendar views.
      </p>
      <div className="duplicate-search">
        <label className="field">
          <span>From</span>
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="field">
          <span>To</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <label className="field duplicate-query">
          <span>Search source records</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <button
          className="button button-quiet"
          disabled={busy || !from || !to}
          onClick={() => void search()}
        >
          Search records
        </button>
      </div>
      {candidates.length > 0 && (
        <div className="duplicate-selectors">
          <label className="field">
            <span>First source record</span>
            <select
              value={firstId}
              onChange={(event) => {
                const value = event.target.value;
                const next = candidates.find((candidate) => candidate.id === value);
                setFirstId(value);
                setRetainedId('');
                setConfirmed(false);
                void inspectLinks([next, second].filter(Boolean) as DuplicateCandidate[]);
              }}
            >
              <option value="">Choose a record</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.title} - {candidate.calendarName}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Second source record</span>
            <select
              value={secondId}
              onChange={(event) => {
                const value = event.target.value;
                const next = candidates.find((candidate) => candidate.id === value);
                setSecondId(value);
                setRetainedId('');
                setConfirmed(false);
                void inspectLinks([first, next].filter(Boolean) as DuplicateCandidate[]);
              }}
            >
              <option value="">Choose a record</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.title} - {candidate.calendarName}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {inspecting && <p role="status">Checking existing relationships…</p>}
      {relatedChoices.length > 0 && !inspecting && (
        <div className="duplicate-decision">
          <p>A selected record already has a reviewed relationship.</p>
          {relatedChoices.map((choice) => (
            <button
              className="button button-quiet"
              key={choice.link.duplicateId}
              onClick={() => {
                inspection.current++;
                setCandidates((current) =>
                  current.some((record) => record.id === choice.counterpart.id)
                    ? current
                    : [...current, choice.counterpart],
                );
                setFirstId(choice.selected.id);
                setSecondId(choice.counterpart.id);
                setRetainedId(choice.link.canonicalId);
                setConfirmed(false);
                setRelatedChoices([]);
                setLink(choice.link);
                setInspecting(false);
              }}
            >
              Review link with {choice.counterpart.title}
            </button>
          ))}
        </div>
      )}
      {pair.length === 2 && (
        <>
          <div className="duplicate-comparison">
            <CandidateDetails candidate={pair[0]!} />
            <CandidateDetails candidate={pair[1]!} />
          </div>
          {link ? (
            <div className="duplicate-decision">
              <p>
                These records are linked. The retained record appears in combined views; neither
                source record was deleted.
              </p>
              <button
                className="button button-quiet"
                disabled={busy}
                onClick={() => void removeLink()}
              >
                Unlink records
              </button>
            </div>
          ) : (
            <fieldset className="duplicate-decision">
              <legend>Choose the record to retain in combined views</legend>
              {pair.map((candidate) => (
                <label key={candidate.id}>
                  <input
                    type="radio"
                    name="retained-event"
                    checked={retainedId === candidate.id}
                    disabled={inspecting || relatedChoices.length > 0}
                    onChange={() => setRetainedId(candidate.id)}
                  />
                  Keep {candidate.title}
                </label>
              ))}
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={inspecting || relatedChoices.length > 0}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                These source records describe the same event
              </label>
              <button
                className="button"
                disabled={
                  busy || inspecting || relatedChoices.length > 0 || !retainedId || !confirmed
                }
                onClick={() => void createLink()}
              >
                Link duplicate records
              </button>
            </fieldset>
          )}
        </>
      )}
      {!busy && candidates.length === 0 && query && <p>No source records matched this search.</p>}
    </section>
  );
}
